import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  Card,
  Table,
  Button,
  Input,
  Pagination,
  Select,
  Space,
  Modal,
  Form,
  Message,
  Dropdown,
  Menu,
  Tooltip,
} from '@arco-design/web-react'
import { IconPlus, IconRefresh, IconSearch, IconEdit, IconMore } from '@arco-design/web-react/icon'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getUsersApi,
  searchUsersApi,
  createUserAdminApi,
  deleteUserAdminApi,
  manageUserApi,
  adminResetUser2FAApi,
  adminResetUserPasskeyApi,
  type AdminUser,
} from '@/api/user'
import { getGroupsApi } from '@/api/group'
import PageHeader from '@/components/Layout/PageHeader'
import EditUserModal from '@/components/users/EditUserModal'
import UserSubscriptionsModal from '@/components/users/UserSubscriptionsModal'
import UserBindingManagementModal from '@/components/users/UserBindingManagementModal'
import SecureVerifyModal from '@/components/Common/SecureVerifyModal'
import { useSecureVerification } from '@/hooks/useSecureVerification'
import { renderRole, renderUserStatus, renderTimestamp } from '@/utils/render'
import { PAGE_SIZE_OPTIONS, USER_ROLE } from '@/utils/constants'
import { renderQuota } from '@/utils/quota'
import { getFormValidateErrorMessage } from '@/utils/helpers'

const ALL_GROUP = '__ALL_GROUP__'

async function loadUsersPage(params: {
  page: number
  pageSize: number
  keyword: string
  group: string
}) {
  const keyword = params.keyword.trim()
  const group = params.group === ALL_GROUP ? '' : params.group
  const query = { p: params.page, page_size: params.pageSize, ...(group ? { group } : {}) }
  if (keyword || group) return searchUsersApi({ ...query, ...(keyword ? { keyword } : {}) })
  return getUsersApi(query)
}

export default function UserPage() {
  const { runSecure, verifyVisible, verifyScope, verifyContext, handleSuccess, handleCancel } =
    useSecureVerification()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [keyword, setKeyword] = useState('')
  const [searchKey, setSearchKey] = useState({ keyword: '', group: ALL_GROUP })
  const [editing, setEditing] = useState<AdminUser | null>(null)
  const [bindingTarget, setBindingTarget] = useState<AdminUser | null>(null)
  const [subUser, setSubUser] = useState<AdminUser | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [createForm] = Form.useForm()
  const tableAreaRef = useRef<HTMLDivElement>(null)
  const [tableBodyScrollY, setTableBodyScrollY] = useState(440)

  const qc = useQueryClient()
  const measureTableBody = useCallback(() => {
    const el = tableAreaRef.current
    if (!el) return
    const theadReserve = 50
    setTableBodyScrollY(Math.max(240, Math.floor(el.clientHeight - theadReserve)))
  }, [])

  useLayoutEffect(() => {
    measureTableBody()
    const el = tableAreaRef.current
    const ro = new ResizeObserver(() => measureTableBody())
    if (el) ro.observe(el)
    window.addEventListener('resize', measureTableBody)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measureTableBody)
    }
  }, [measureTableBody])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['users', { page, pageSize, ...searchKey }],
    queryFn: () =>
      loadUsersPage({ page, pageSize, keyword: searchKey.keyword, group: searchKey.group }),
  })
  const groupsQuery = useQuery({
    queryKey: ['groups', 'admin-user-options'],
    queryFn: getGroupsApi,
  })
  const createMut = useMutation({
    mutationFn: (data: {
      username: string
      password: string
      display_name?: string
      role?: number
    }) =>
      runSecure((proof) => createUserAdminApi(data, proof), {
        scope: 'admin.user.create',
        context: { role: data.role ?? USER_ROLE.COMMON },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
  const deleteMut = useMutation({
    mutationFn: (id: number) =>
      runSecure((proof) => deleteUserAdminApi(id, proof), {
        scope: 'admin.user.delete',
        context: { user_id: id },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })

  const items = useMemo(() => data?.items ?? [], [data?.items])
  const total = data?.total || 0
  const groupOptions = useMemo(() => {
    const groups = new Set<string>(groupsQuery.data || [])
    items.forEach((item) => {
      if (item.group) groups.add(item.group)
    })
    return Array.from(groups).sort((a, b) => a.localeCompare(b))
  }, [groupsQuery.data, items])

  useLayoutEffect(() => {
    if (!isLoading) measureTableBody()
  }, [isLoading, items.length, measureTableBody])

  const handleSearch = () => {
    setPage(1)
    setSearchKey({ keyword: keyword.trim(), group: searchKey.group })
  }

  const handleGroupChange = (value: string | undefined) => {
    setSearchKey((current) => ({ ...current, group: value || ALL_GROUP }))
    setPage(1)
  }

  const handleCreate = async () => {
    try {
      const v = await createForm.validate()
      await createMut.mutateAsync(v)
      Message.success('已创建')
      setCreateOpen(false)
      createForm.resetFields()
    } catch (err: unknown) {
      Message.error(getFormValidateErrorMessage(err))
    }
  }

  const manageWithConfirm = (
    row: AdminUser,
    action: 'enable' | 'disable' | 'promote' | 'demote' | 'delete',
    label: string
  ) => {
    Modal.confirm({
      title: `确认对用户 ${row.username} 执行「${label}」？`,
      onOk: async () => {
        try {
          // 后端按 id 操作，并且需要 admin.user.manage 的二次验证凭据
          await runSecure((proof) => manageUserApi({ id: row.id, action }, proof), {
            scope: 'admin.user.manage',
            context: { user_id: row.id, action },
          })
          Message.success('操作成功')
          refetch()
        } catch (err: unknown) {
          Message.error(err instanceof Error ? err.message : '操作失败')
        }
      },
    })
  }

  const actionMenu = (row: AdminUser) => (
    <Menu
      onClickMenuItem={(key) => {
        if (key === 'enable') manageWithConfirm(row, 'enable', '启用')
        else if (key === 'disable') manageWithConfirm(row, 'disable', '禁用')
        else if (key === 'promote') manageWithConfirm(row, 'promote', '提升为管理员')
        else if (key === 'demote') manageWithConfirm(row, 'demote', '降级')
        else if (key === 'bindings') setBindingTarget(row)
        else if (key === 'subscriptions') setSubUser(row)
        else if (key === 'reset-2fa') {
          Modal.confirm({
            title: `重置用户 ${row.username} 的 2FA？`,
            onOk: () =>
              runSecure((proof) => adminResetUser2FAApi(row.id, proof), {
                scope: 'admin.user.2fa.disable',
                context: { user_id: row.id },
              })
                .then(() => Message.success('已重置'))
                .catch((e) => Message.error(e.message)),
          })
        } else if (key === 'reset-passkey') {
          Modal.confirm({
            title: `重置用户 ${row.username} 的 Passkey？`,
            onOk: () =>
              runSecure((proof) => adminResetUserPasskeyApi(row.id, proof), {
                scope: 'admin.user.passkey.reset',
                context: { user_id: row.id },
              })
                .then(() => Message.success('已重置'))
                .catch((e) => Message.error(e.message)),
          })
        } else if (key === 'delete') {
          Modal.confirm({
            title: `确认删除用户 ${row.username}？此操作不可逆`,
            onOk: async () => {
              await deleteMut.mutateAsync(row.id)
              Message.success('已删除')
            },
          })
        }
      }}
    >
      {row.status === 1 ? (
        <Menu.Item key="disable">禁用</Menu.Item>
      ) : (
        <Menu.Item key="enable">启用</Menu.Item>
      )}
      {row.role < USER_ROLE.ADMIN ? (
        <Menu.Item key="promote">提升为管理员</Menu.Item>
      ) : row.role < USER_ROLE.ROOT ? (
        <Menu.Item key="demote">降级为普通用户</Menu.Item>
      ) : null}
      <Menu.Item key="reset-2fa">重置 2FA</Menu.Item>
      <Menu.Item key="reset-passkey">重置 Passkey</Menu.Item>
      <Menu.Item key="bindings">第三方绑定</Menu.Item>
      <Menu.Item key="subscriptions">订阅管理</Menu.Item>
      {row.role < USER_ROLE.ROOT ? <Menu.Item key="delete">删除</Menu.Item> : null}
    </Menu>
  )

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: '5%',
      ellipsis: true,
    },
    {
      title: '用户名 / 显示名',
      width: '15%',
      ellipsis: true,
      render: (_: unknown, row: AdminUser) => (
        <span
          className="block min-w-0 truncate text-sm"
          title={`${row.username || '-'} / ${row.displayName || '-'}`}
        >
          {row.username || '-'} / {row.displayName || '-'}
        </span>
      ),
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      width: '15%',
      ellipsis: true,
      render: (v: string) => v || <span className="text-gray-400">-</span>,
    },
    {
      title: '角色',
      dataIndex: 'role',
      width: '10%',
      render: renderRole,
    },
    { title: '状态', dataIndex: 'status', width: '10%', render: renderUserStatus },
    {
      title: '分组',
      dataIndex: 'group',
      width: '10%',
      ellipsis: true,
      render: (v?: string) => <span className="block min-w-0 truncate">{v || 'default'}</span>,
    },
    {
      title: '额度 / 已用',
      width: '10%',
      ellipsis: true,
      render: (_: unknown, row: AdminUser) => (
        <span
          className="block min-w-0 truncate text-sm"
          title={`${renderQuota(row.quota)} / ${renderQuota(row.usedQuota)}`}
        >
          {renderQuota(row.quota)} / {renderQuota(row.usedQuota)}
        </span>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdTime',
      width: '8%',
      render: (_: unknown, row: AdminUser) => (
        <span className="block min-w-0 text-xs text-gray-600 [&_span]:truncate">
          {row.createdTime ? renderTimestamp(row.createdTime) : '-'}
        </span>
      ),
    },
    {
      title: '操作',
      width: '10%',
      render: (_: unknown, row: AdminUser) => (
        <Space size={2} className="!flex-nowrap">
          <Tooltip content="编辑">
            <Button
              size="small"
              type="text"
              icon={<IconEdit />}
              aria-label="编辑"
              onClick={() => setEditing(row)}
            />
          </Tooltip>
          <Dropdown droplist={actionMenu(row)} trigger="click">
            <Button size="small" type="text" icon={<IconMore />} aria-label="更多操作" />
          </Dropdown>
        </Space>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title="用户管理"
        subtitle="服务端搜索、分组筛选、编辑与角色管理"
        actions={
          <Space>
            <Input
              placeholder="搜索 ID / 用户名 / 邮箱 / 显示名"
              value={keyword}
              onChange={setKeyword}
              onPressEnter={handleSearch}
              style={{ width: 260 }}
              prefix={<IconSearch />}
              allowClear
            />
            <Select
              placeholder="全部用户组"
              value={searchKey.group}
              onChange={handleGroupChange}
              style={{ width: 180 }}
              loading={groupsQuery.isLoading}
              allowClear
            >
              <Select.Option value={ALL_GROUP}>全部用户组</Select.Option>
              {groupOptions.map((group) => (
                <Select.Option key={group} value={group}>
                  {group}
                </Select.Option>
              ))}
            </Select>
            <Button type="primary" icon={<IconSearch />} onClick={handleSearch}>
              查询
            </Button>
            <Button icon={<IconRefresh />} onClick={() => refetch()}>
              刷新
            </Button>
            <Button type="primary" icon={<IconPlus />} onClick={() => setCreateOpen(true)}>
              新建用户
            </Button>
          </Space>
        }
      />
      <Card
        className="!rounded-2xl !border-[#EAECEF] !shadow-[0_4px_20px_rgba(15,23,42,0.04)] flex h-[calc(100vh-13rem)] min-h-[300px] flex-col"
        bordered={false}
        bodyStyle={{
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'hidden',
          height: '100%',
        }}
      >
        <div ref={tableAreaRef} className="min-h-0 min-w-0 flex-1 overflow-hidden">
          <Table
            tableLayoutFixed
            rowKey="id"
            loading={isLoading}
            columns={columns}
            data={items}
            scroll={{ x: 1200, y: tableBodyScrollY }}
            pagination={false}
            borderCell={false}
            stripe={false}
            className="[&_.arco-table-container]:max-w-full [&_.arco-table-th]:!py-3 [&_.arco-table-td]:!py-3"
          />
        </div>
        <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-x-4 gap-y-2 rounded-b-2xl border-t border-[#EAECEF] bg-[#FAFBFC] px-4 py-3">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showTotal={(count, range) =>
              count > 0 ? `第 ${range[0]}-${range[1]} 条，共 ${count} 条` : `共 ${count} 条`
            }
            sizeCanChange
            pageSizeChangeResetCurrent
            sizeOptions={PAGE_SIZE_OPTIONS}
            onChange={(p, ps) => {
              setPage(p)
              setPageSize(ps)
            }}
          />
        </div>
      </Card>

      <EditUserModal
        visible={!!editing}
        user={editing}
        groupOptions={groupOptions}
        onCancel={() => setEditing(null)}
        onSuccess={refetch}
      />

      <UserBindingManagementModal
        visible={!!bindingTarget}
        user={bindingTarget}
        onCancel={() => setBindingTarget(null)}
      />

      <UserSubscriptionsModal
        visible={!!subUser}
        user={subUser}
        onCancel={() => setSubUser(null)}
        onSuccess={refetch}
      />

      <Modal
        title="新建用户"
        visible={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
        confirmLoading={createMut.isPending}
      >
        <Form form={createForm} layout="vertical">
          <Form.Item field="username" label="用户名" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item field="password" label="密码" rules={[{ required: true }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item field="display_name" label="显示名">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <SecureVerifyModal
        visible={verifyVisible}
        scope={verifyScope}
        context={verifyContext}
        onCancel={handleCancel}
        onSuccess={handleSuccess}
        title="该操作需要安全验证"
      />
    </div>
  )
}
